// Shared helpers of the player pages' commands (spec/dashboard.md section 11.2): restores, resets and erases are commands,
// never staged, and are refused while the player has staged edits; the bump text of the toasts; the error toast.
import { ApiError } from '../../api/client';
import type { Toasts } from '../../ui/Toast';
import type { Workspace } from '../../workspace/Workspace';

export const STAGED_REFUSAL = 'Save or discard the staged edits of this player first';

export const SOURCE_LABELS = ['client', 'admin', 'restore', 'reset', 'import', 'delete'] as const;

/** True when the environment workspace holds a `player-key` draft of this player (section 11.4). */
export function hasStagedEdits(workspace: Workspace, env: string, playerId: string): boolean {
  const prefix = `${env}/${playerId}/`;
  return workspace.all.some((d) => d.kind === 'player-key' && d.id.startsWith(prefix));
}

/** Toasts the refusal and returns false when the player has staged edits; true when the command may run. */
export function guardStaged(workspace: Workspace, env: string, playerId: string, toasts: Toasts): boolean {
  if (!hasStagedEdits(workspace, env, playerId)) return true;
  toasts.error(STAGED_REFUSAL);
  return false;
}

export interface Bump {
  collection: string;
  from: number | string;
  to: number | string;
}

/** `UnnyProfile unnySystem 5 → 1005` */
export function bumpText(bump: Bump | null | undefined): string {
  return bump ? `${bump.collection} unnySystem ${bump.from} → ${bump.to}` : '';
}

/** The message of a failed command; the online guard (409 `player_online`) says how to override. */
export function commandErrorText(e: unknown): string {
  if (e instanceof ApiError) {
    const online = e.code === 'player_online' || (e.details ?? []).some((d) => d.code === 'player_online');
    return online ? `${e.message} Tick "Force" to override.` : e.message;
  }
  return e instanceof Error ? e.message : String(e);
}

/** The error toast of a command. */
export function toastCommandError(toasts: Toasts, e: unknown): void {
  toasts.error(commandErrorText(e), e instanceof ApiError ? { requestId: e.requestId } : undefined);
}

/** The history's time ranges (spec/dashboard.md section 11.2): the chip keys of the drawer and their spans. */
export const HISTORY_SINCE: Record<string, { ms: number; label: string }> = {
  '24h': { ms: 24 * 3600_000, label: 'last 24 hours' },
  '7d': { ms: 7 * 24 * 3600_000, label: 'last 7 days' },
  '30d': { ms: 30 * 24 * 3600_000, label: 'last 30 days' },
};

/** One restore's outcome in a whole-player restore. */
export interface RestoreOutcome {
  collection: string;
  /** keys restored; null while failed */
  restored: number | null;
  bump?: string;
  error?: string;
}

/** "Restored UnnyProfile (12 keys), BjbUser (4 keys); session_guard already held those values" */
export function wholeRestoreSummary(outcomes: RestoreOutcome[]): string {
  const done = outcomes.filter((o) => o.restored !== null);
  const changed = done.filter((o) => o.restored! > 0).map((o) => `${o.collection} (${o.restored} key${o.restored === 1 ? '' : 's'})`);
  const same = done.filter((o) => o.restored === 0).map((o) => o.collection);
  const held = same.length ? `${same.join(', ')} already held those values` : '';
  const bumps = done.map((o) => o.bump).filter(Boolean);
  const head = changed.length ? `Restored ${changed.join(', ')}${held ? `; ${held}` : ''}` : `Nothing to restore: ${held || 'no collection'}`;
  return `${head}${bumps.length ? ` · ${bumps.join(', ')}` : ''}`;
}

export function errorText(e: unknown): string {
  return e instanceof ApiError ? e.message : e instanceof Error ? e.message : String(e);
}

/** True while the ban holds: set, and without an end or ending in the future (spec/api-conventions.md section 21.3). */
export function banActive(ban: { until: string | null } | null | undefined, now = Date.now()): boolean {
  if (!ban) return false;
  return !ban.until || Date.parse(ban.until) > now;
}
